import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Badge, Tooltip, ButtonGroup, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { MailOutline as MailOutlineIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { frFR, enUS } from '@mui/x-data-grid/locales';
import { formatDateTime, getGenderName, showDeleteResults } from '@utils/helpers';
import API from '@/utils/axios';
import theme from '@/theme';
import { useAuth } from '@contexts/AuthContext';
import useLocalStorage from '@/hooks/useLocalStorage';
import OurPagination from '@components/OurPagination';
import OurDataToolbar from '@components/OurDataToolbar';
import ContactSearch from '@components/contact/ContactSearch';
import ContactExtra from '@components/contact/ContactExtra';
import SendoutContactStatusChip from '@components/sendout/SendoutContactStatusChip';
import { useSnackbar } from '@contexts/SnackbarContext';
import { useDialog } from '@contexts/DialogContext';

function ContactTable({ url, onRowClick, storageKey = 'contacts', withSendoutColumns = false, withDeleteButton = true, onDeleted = () => {}, ...props }) {
    const { t, i18n } = useTranslation();
    const { doLogout } = useAuth();
    const { showMessage } = useSnackbar();
    const { requestConfirm } = useDialog();
    const [loading, setLoading] = useState(true);
    const [contacts, setContacts] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [selectedRows, setSelectedRows] = useState([]);
    const [paginationModel, setPaginationModel] = useLocalStorage(`${storageKey}.pagination`, {
        pageSize: 10,
        page: 0,
    });
    const [sortModel, setSortModel] = useLocalStorage(`${storageKey}.sort`, [{
        field: 'full_name',
        sort: 'asc',
    }]);
    const [columnVisibility, setColumnVisibility] = useLocalStorage(`${storageKey}.columns`, {
        first_name: false,
        last_name: false,
        lang: false,
        created_at: false,
        updated_at: false,
        extra: false,
    });
    const [density, setDensity] = useLocalStorage(`${storageKey}.density`, 'standard');
    const [searchVisible, setSearchVisible] = useLocalStorage(`${storageKey}.searchVisible`, false);
    const [filter, setFilter] = useLocalStorage(`${storageKey}.searchValues`, {});

    const toggleSearch = () => {
        if (searchVisible) {
            setFilter({});
        }
        setSearchVisible(!searchVisible);
    };

    const getFullName = (value, row) => {
        return `${row.first_name || ''} ${row.last_name || ''}`;
    };

    const loadContacts = useCallback(async (paginationModel, sortModel) => {
        try {
            setLoading(true);
            const { field = '', sort = '' } = sortModel[0] || {};
            let params = {
                per_page: paginationModel.pageSize,
                page: paginationModel.page + 1,
                sort: field,
                direction: sort,
            };
            const response = await API.get(url, { params: { ...params, ...filter } });
            setContacts(
                response.data.data.map((contact) => ({
                    id: contact.id,
                    gender: contact.gender,
                    first_name: contact.first_name,
                    last_name: contact.last_name,
                    email: contact.email,
                    phone: contact.phone,
                    lang: contact.lang,
                    extra: contact.extra,
                    created_at: new Date(contact.created_at),
                    updated_at: new Date(contact.updated_at),
                    status: withSendoutColumns ? contact.status : null,
                    count: withSendoutColumns ? contact.count : null,
                }))
            );
            setRowCount(response.data.total);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            if (error.response && error.response.status === 401) {
                doLogout();
            } else {
                console.error('Erreur de chargement', error);
            }
        }
    }, [url, withSendoutColumns, doLogout, filter]);

    useEffect(() => {
        loadContacts(paginationModel, sortModel);
    }, [loadContacts, paginationModel, sortModel]);

    const localeTheme = i18n.language === 'fr' ? frFR : enUS;
    const themeWithLocale = createTheme(theme, localeTheme);

    const columns = [
        {
            field: 'id',
            headerName: t('ID'),
            flex: 0.5,
            type: 'number',
            maxWidth: 70,
        },
        {
            field: 'gender',
            headerName: t('Gender'),
            flex: 0.5,
            type: 'string',
            valueFormatter: (gender) => getGenderName(t, gender),
        },
        {
            field: 'first_name',
            headerName: t('First name'),
            flex: 1,
            type: 'string'
        },
        {
            field: 'last_name',
            headerName: t('Last name'),
            flex: 1,
            type: 'string'
        },
        {
            field: 'full_name',
            headerName: t('Full name'),
            flex: 2,
            type: 'string',
            valueGetter: getFullName
        },
        {
            field: 'email',
            headerName: t('Email'),
            flex: 2,
            type: 'string'
        },
        {
            field: 'phone',
            headerName: t('Phone'),
            flex: 2,
            type: 'string'
        },
        ...(withSendoutColumns ? [
            {
                field: 'status',
                headerName: t('Status'),
                flex: 0.5,
                type: 'string',
                renderCell: (params) => <SendoutContactStatusChip status={params.value} />
            },
            {
                field: 'messages',
                headerName: t('Messages'),
                sortable: false,
                flex: 0.5,
                type: 'string',
                renderCell: (params) => {
                    const nb = params.row.count.messages;

                    return (
                        <div style={{ marginTop: 4 }}>
                            {/* Icône pour les messages envoyés */}
                            {nb > 0 && (
                                <Tooltip title={nb > 1 ? t('{{count}} messages sent', { count: nb }) : t('1 message sent')}>
                                    <Badge badgeContent={nb} color="grey" size="small"
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                backgroundColor: theme.palette.grey[300]
                                            }
                                        }} >
                                        <MailOutlineIcon />
                                    </Badge>
                                </Tooltip>
                            )
                            }
                        </div >
                    );
                }
            }
        ] : []),
        {
            field: 'lang',
            headerName: t('Language'),
            flex: 1,
            type: 'string',
            valueFormatter: (lang) => lang ? t('lang.' + lang) : ''
        },
        ...(!withSendoutColumns ? [
            {
                field: 'created_at',
                headerName: t('Creation date'),
                flex: 1,
                type: 'dateTime',
                valueFormatter: formatDateTime
            },
            {
                field: 'updated_at',
                headerName: t('Update date'),
                flex: 1,
                type: 'dateTime',
                valueFormatter: formatDateTime
            },
        ] : []),
        {
            field: 'extra',
            headerName: t('Extra data'),
            flex: 3,
            type: 'string',
            renderCell: (params) => <ContactExtra extra={params.value} />
        }
    ];

    const handleDelete = async () => {
        requestConfirm({
            message: t('Do you really want to delete the selected contacts?'), 
            onConfirm: async () => {
                try {
                    const results = await API.delete(`${url}/batch`, { data: { ids: selectedRows } });
                    showDeleteResults(results, showMessage, t, '{{nb}} contact deleted successfully', '{{nb}} contacts deleted successfully');
                    loadContacts(paginationModel, sortModel); // Refresh
                    onDeleted();
                } catch (error) {
                    if (error.response && error.response.status === 401) {
                        doLogout();
                    } else {
                        showMessage(t(error.message), 'error');
                    }
                }
            }
        });
    };

    return (
        <ThemeProvider theme={themeWithLocale}>
            <Paper {...props}>
                {searchVisible && <ContactSearch handleSubmit={setFilter} filter={filter} />}
                <DataGrid
                    autoHeight
                    loading={loading}
                    slots={{
                        toolbar: OurDataToolbar,
                        pagination: OurPagination,
                    }}
                    slotProps={{
                        toolbar: { searchVisible, toggleSearch },
                    }}
                    initialState={{
                        sorting: { sortModel }
                    }}
                    rows={contacts}
                    columns={columns}
                    pageSizeOptions={[10, 15, 20, 50, 100]}
                    pagination
                    paginationMode="server"
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    columnVisibilityModel={columnVisibility}
                    onColumnVisibilityModelChange={(newModel) =>
                        setColumnVisibility(newModel)
                    }
                    sortingMode="server"
                    sortingOrder={['asc', 'desc']}
                    onSortModelChange={setSortModel}
                    density={density}
                    onDensityChange={setDensity}
                    disableRowSelectionOnClick
                    disableSelectionOnClick={!withDeleteButton}
                    checkboxSelection={withDeleteButton}
                    onRowSelectionModelChange={(newSelectionModel) => {
                        setSelectedRows(newSelectionModel);
                    }}
                    disableColumnResize={true}
                    disableColumnFilter
                    onRowClick={onRowClick}
                />
            </Paper>

            {withDeleteButton && (
                <ButtonGroup variant="contained" aria-label="Actions" sx={{ mt: 2 }}>
                    <Button onClick={handleDelete} startIcon={<DeleteIcon />}
                        disabled={selectedRows.length === 0}>
                        {t('Delete')}
                    </Button>
                </ButtonGroup>
            )}

        </ThemeProvider>
    );
}

export default ContactTable;
